import { vomStartbildschirm } from './betriebsart'
import { istAppleMitFinger } from './geraet'

/**
 * Die App auf den Startbildschirm legen (#101, 1.56.0).
 *
 * **Zwei Wege, und sie sind nicht dieselben.** Chromium meldet von sich aus,
 * dass die Seite installierbar ist (`beforeinstallprompt`), und lässt die
 * Anwendung den Systemdialog öffnen. Safari tut das nicht: dort geht es nur
 * über Teilen und „Zum Home-Bildschirm", von Hand, und keine Zeile Code kann
 * diesen Dialog aufmachen. Der Knopf führt deshalb auf iOS zu einer
 * Anleitung und sonst zum Dialog.
 *
 * **Das Ereignis kommt früh, oft vor dem ersten Zeichnen.** Es wird deshalb
 * hier auf Modulebene abgefangen und aufbewahrt, nicht in einer Komponente:
 * wer zu spät zuhört, hört es nie. `main.tsx` importiert diese Datei, damit
 * das Zuhören mit dem Start beginnt.
 */

/** Was der Browser mitgibt: der aufgehobene Dialog. */
interface Installationsangebot extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

let angebot: Installationsangebot | null = null
let schonInstalliert = false
const hoerer = new Set<() => void>()

function melden(): void {
  for (const h of hoerer) h()
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (ereignis) => {
    // Ohne das hielte Chromium seinen eigenen Zeitpunkt für den richtigen.
    ereignis.preventDefault()
    angebot = ereignis as Installationsangebot
    melden()
  })
  window.addEventListener('appinstalled', () => {
    angebot = null
    schonInstalliert = true
    melden()
  })
}

/** Auf Änderungen hören (React zeichnet dann neu). */
export function abonniereInstallation(bei: () => void): () => void {
  hoerer.add(bei)
  return () => hoerer.delete(bei)
}

/**
 * Soll der Eintrag überhaupt dastehen?
 *
 * Nur, wenn die Anwendung nicht ohnehin schon vom Startbildschirm läuft und
 * in dieser Sitzung nichts installiert wurde. Gefragt wird mit
 * `vomStartbildschirm()` und nicht mit `alsApp()`: ein In-App-Browser meldet
 * sonst „bin schon App" und nähme den Knopf dem weg, der ihn braucht. Auf iOS steht er immer, denn dort kann
 * niemand fragen, ob es schon geht; anderswo erst, wenn der Browser sein
 * Angebot gemacht hat -- ein Knopf, der nichts tut, ist schlechter als
 * keiner.
 */
export function installationMoeglich(): boolean {
  if (vomStartbildschirm() || schonInstalliert) return false
  return istAppleMitFinger() || angebot !== null
}

/** Führt der Knopf zum Systemdialog oder zur Anleitung? */
export function fuehrtZurAnleitung(): boolean {
  return angebot === null
}

/**
 * Den Systemdialog öffnen.
 *
 * @returns was daraus wurde: angenommen, abgelehnt, oder es gab nichts zu
 *   öffnen -- dann zeigt der Aufrufer die Anleitung.
 */
export async function installationAnbieten(): Promise<'angenommen' | 'abgelehnt' | 'anleitung'> {
  const jetzt = angebot
  if (jetzt === null) return 'anleitung'
  /*
   * **Auch nach „Abbrechen" ist das Angebot verbraucht** -- und deshalb wird es
   * hier weggeräumt, gleich wie die Wahl ausfiel (nachgemessen 1.66.0, weil die
   * Codereview es für einen Fehler hielt).
   *
   * Der Browser gibt dieses Ereignis **einmal**; `prompt()` ein zweites Mal
   * darauf weist ab. Ein stehengelassener Knopf führte also in eine
   * Fehlermeldung statt in den Dialog, und „ein Knopf, der nichts tut, ist
   * schlechter als keiner" steht schon an `installationMoeglich()`. Chromium
   * bietet von selbst wieder an, beim nächsten Besuch -- innerhalb dieser
   * Seite nicht.
   *
   * Der Eintrag verschwindet damit nach einem Abbrechen, und das ist keine
   * stille Absage: der Weg über das Browsermenü bleibt, und das Handbuch
   * beschreibt ihn (Android: drei Punkte, „App installieren").
   */
  try {
    await jetzt.prompt()
    const wahl = await jetzt.userChoice
    return wahl.outcome === 'accepted' ? 'angenommen' : 'abgelehnt'
  } catch {
    // **Ein abgewiesener Dialog ist kein Absturz.** Zwei Tipps kurz
    // hintereinander, und der zweite landet hier; ohne dieses `catch` wäre es
    // eine unbehandelte Zurückweisung und für den Menschen gar nichts.
    return 'abgelehnt'
  } finally {
    angebot = null
    melden()
  }
}
